import React, { useEffect, useMemo, useState } from 'react'
import { debounce } from 'lodash'
import { Avatar, Checkbox, Dropdown, Spin } from 'antd'
import { ItemType } from 'antd/lib/menu/hooks/useItems'
import {
  CaretDown,
  CaretLeft,
  CaretRight,
  MagnifyingGlass,
  DotsSix,
} from '@phosphor-icons/react'
import Down from 'helper/Icons/DownIcon'
import { infoMessage } from 'api/global'
import { getNameSignature } from 'helper/utility'
import { useGetAllOfficials } from 'api/league/LeagueCreation/leagueOfficials'
import { useGetLeagueAssignedMentors } from 'api/league/LeagueManage/leagueManage'
import { useGetLeagueGames } from 'api/league/leagueGames/leagueGames'
import { useGetLeaguePracticeSchedule } from 'api/league/LeaguePractice/leaguePractice'
import RequestMentorModal from './Modals/RequestMentorModal'
import { leagueStaffStore } from 'state/league/leagueStaff'
import { leagueEventStore } from 'state/league/leagueEvent'
import { StaffSession, StaffMember } from 'types/league/leagueStaff'
import '../../styles/stafftab.scss'
import type { MenuProps } from 'antd'

/* ── Schedule constants ─────────────────────────────── */
const SCHEDULE_START_HOUR = 9
const SCHEDULE_END_HOUR = 16
const CELL_PX = 70
const CELLS_PER_HOUR = 2

const TIME_SLOTS: string[] = Array.from(
  { length: SCHEDULE_END_HOUR - SCHEDULE_START_HOUR },
  (_, i) => {
    const h = SCHEDULE_START_HOUR + i
    const ampm = h >= 12 ? 'PM' : 'AM'
    const dh = h > 12 ? h - 12 : h
    return `${dh}:00 ${ampm}`
  },
)

const TOTAL_CELLS = (SCHEDULE_END_HOUR - SCHEDULE_START_HOUR) * CELLS_PER_HOUR

/* ── Helpers ────────────────────────────────────────── */
const sessionLeft = (startHour: number): number =>
  (startHour - SCHEDULE_START_HOUR) * CELLS_PER_HOUR * CELL_PX

const sessionWidth = (startHour: number, endHour: number): number =>
  (endHour - startHour) * CELLS_PER_HOUR * CELL_PX

const fmtHour = (hour: number): string => {
  const h = Math.floor(hour)
  const m = Math.round((hour - h) * 60)
  const ampm = h >= 12 ? 'pm' : 'am'
  const dh = h > 12 ? h - 12 : h
  return `${dh.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')} ${ampm}`
}

const getWeekStart = (d: Date): Date => {
  const day = d.getDay()
  const start = new Date(d)
  start.setDate(d.getDate() - ((day + 6) % 7))
  start.setHours(0, 0, 0, 0)
  return start
}

const getWeekEnd = (monday: Date): Date => {
  const end = new Date(monday)
  end.setDate(monday.getDate() + 6)
  end.setHours(23, 59, 59, 999)
  return end
}

const formatWeekRange = (monday: Date): string => {
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  const fmt = (dt: Date, year?: boolean) =>
    dt.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(year ? { year: 'numeric' } : {}),
    })
  return `${fmt(monday)} - ${fmt(sunday, true)}`
}

/* Convert a Unix-ms timestamp to decimal hours (e.g. 9.5 = 9:30 AM) */
const tsToHour = (ts: number): number => {
  const d = new Date(ts)
  return d.getHours() + d.getMinutes() / 60
}

/* Clamp session to the visible grid range */
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

/* ── Position options ──────────────────────────────── */
const POSITION_OPTIONS = [
  { label: 'Referee', value: 'referee' },
  { label: 'Score Keeper', value: 'score-keeper' },
  { label: 'Coach', value: 'coach' },
]

const positionLabels: Record<string, string> = {
  all: 'Positions',
  referee: 'Referee',
  'score-keeper': 'Score Keeper',
  coach: 'Coach',
}

/* ── Component ──────────────────────────────────────── */
const StaffTab: React.FC = () => {
  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))
  const leagueId = leagueData?._id || ''
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [positionFilter, setPositionFilter] = useState('all')
  const [pendingPosition, setPendingPosition] = useState<string | undefined>(undefined)
  const [filterOpen, setFilterOpen] = useState(false)
  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(new Date()))

  const debouncedSetSearch = useMemo(
    () => debounce((q: string) => setSearchTerm(q), 500),
    [],
  )

  const { setRequestMentorModal } = leagueStaffStore(store => ({
    setRequestMentorModal: store.setRequestMentorModal,
  }))

  const {
    data: officialsData,
    isLoading: officialsLoading,
    refetch: refetchOfficials,
  } = useGetAllOfficials(leagueId!)

  const {
    data: mentorsData,
    isLoading: mentorsLoading,
    refetch: refetchMentors,
  } = useGetLeagueAssignedMentors(leagueId!)

  const {
    data: gamesData,
    isLoading: gamesLoading,
    refetch: refetchGames,
  } = useGetLeagueGames({ leagueId: leagueId! })

  const {
    data: practicesData,
    isLoading: practicesLoading,
  } = useGetLeaguePracticeSchedule({ leagueId: leagueId! })

  useEffect(() => {
    if (leagueId) {
      refetchOfficials()
      refetchMentors()
      refetchGames()
    }
  }, [leagueId])

  const isLoading = officialsLoading || mentorsLoading || gamesLoading || practicesLoading

  /* ── Build sessions for the current week ── */
  const weekEnd = getWeekEnd(weekStart)

  const weekGameSessions: StaffSession[] = (gamesData || [])
    .flatMap(round => round.dates)
    .filter(d => {
      const date = new Date(d.date)
      return date >= weekStart && date <= weekEnd
    })
    .flatMap(d =>
      d.games.map(game => {
        const start = clamp(tsToHour(game.timings.startTime), SCHEDULE_START_HOUR, SCHEDULE_END_HOUR)
        const end = clamp(tsToHour(game.timings.endTime), SCHEDULE_START_HOUR, SCHEDULE_END_HOUR)
        return {
          id: game._id,
          title: `${game.homeTeam?.name || 'Home'} vs ${game.awayTeam?.name || 'Away'}`,
          startHour: start,
          endHour: end > start ? end : start + 1,
          color: 'purple' as const,
        }
      }),
    )

  const weekPracticeSessions: StaffSession[] = (practicesData || [])
    .filter(p => {
      const date = new Date(p.date)
      return date >= weekStart && date <= weekEnd
    })
    .map(p => {
      const start = clamp(tsToHour(p.timings.startTime), SCHEDULE_START_HOUR, SCHEDULE_END_HOUR)
      const end = clamp(tsToHour(p.timings.endTime), SCHEDULE_START_HOUR, SCHEDULE_END_HOUR)
      return {
        id: p._id,
        title: 'Practice',
        startHour: start,
        endHour: end > start ? end : start + 1,
        color: 'orange' as const,
      }
    })

  const allWeekSessions = [...weekGameSessions, ...weekPracticeSessions]

  /* ── Build staff list ── */
  const officials: StaffMember[] = (officialsData || []).map(o => ({
    key: o._id,
    name: o.name,
    role: o.role === 'refree' ? 'Referee' : 'Score Keeper',
    sessions: allWeekSessions,
  }))

  const mentors: StaffMember[] = (mentorsData || []).map(m => ({
    key: m._id,
    name: m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim() || 'Mentor',
    role: 'Coach',
    sessions: allWeekSessions,
  }))

  const allStaff: StaffMember[] = [...officials, ...mentors]

  /* ── Filtering ── */
  const filteredStaff = allStaff.filter(member => {
    const matchesSearch = member.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
    const matchesPosition =
      positionFilter === 'all' ||
      member.role.toLowerCase().replace(' ', '-') === positionFilter ||
      (positionFilter === 'coach' && member.role === 'Coach') ||
      (positionFilter === 'referee' && member.role === 'Referee') ||
      (positionFilter === 'score-keeper' && member.role === 'Score Keeper')
    return matchesSearch && matchesPosition
  })

  const goWeek = (delta: number) => {
    const next = new Date(weekStart)
    next.setDate(weekStart.getDate() + delta * 7)
    setWeekStart(next)
  }

  const moreItems: MenuProps['items'] = [
    {
      key: 'export',
      label: 'Export staff list',
      onClick: () => infoMessage('Export — coming soon'),
    },
    {
      key: 'print',
      label: 'Print roster',
      onClick: () => infoMessage('Print — coming soon'),
    },
    {
      key: 'contact',
      label: 'Contact all',
      onClick: () => infoMessage('Contact all — coming soon'),
    },
  ]

  const viewItems: MenuProps['items'] = [
    { key: 'week', label: 'Week' },
    { key: 'day', label: 'Day' },
    { key: 'month', label: 'Month' },
  ]

  // sync pending when dropdown opens
  useEffect(() => {
    if (filterOpen) {
      setPendingPosition(positionFilter === 'all' ? undefined : positionFilter)
    }
  }, [filterOpen])

  const positionItems: ItemType[] = [
    {
      key: 'pos-content',
      label: (
        <>
          <div className="filter-body">
            <Checkbox.Group
              className="upd-checkbox-group"
              value={pendingPosition ? [pendingPosition] : []}
              onChange={vals =>
                setPendingPosition(vals[vals.length - 1] as string | undefined)
              }
            >
              {POSITION_OPTIONS.map(({ label, value }) => (
                <Checkbox key={value} value={value}>{label}</Checkbox>
              ))}
            </Checkbox.Group>
          </div>
          <div className="filter-footer">
            <button
              className="clr-btn"
              onClick={() => {
                setPendingPosition(undefined)
                setPositionFilter('all')
                setFilterOpen(false)
              }}
            >
              Clear
            </button>
            <button
              className="aply-btn"
              onClick={() => {
                setPositionFilter(pendingPosition || 'all')
                setFilterOpen(false)
              }}
            >
              Apply
            </button>
          </div>
        </>
      ),
    },
  ]

  return (
    <div className="staff-tab-container">
      {/* ── Top action bar ──────────────────────────── */}
      <div className="staff-top-bar">
        <div className="staff-search-wrap">
          <MagnifyingGlass size={16} color="#939598" className="staff-search-icon" />
          <input
            placeholder="Search for mentor"
            value={searchInput}
            onChange={e => {
              setSearchInput(e.target.value)
              debouncedSetSearch(e.target.value)
            }}
          />
        </div>

        <div className="staff-top-actions">
          <Dropdown
            menu={{ items: moreItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <button className="staff-more-btn">
              More <CaretDown size={12} />
            </button>
          </Dropdown>

          <Dropdown
            menu={{ items: viewItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <button className="staff-week-btn">
              Week <CaretDown size={12} />
            </button>
          </Dropdown>

          <button
            className="staff-request-btn"
            onClick={() => setRequestMentorModal(true)}
          >
            Request a mentor
          </button>
        </div>
      </div>

      {/* ── Filter + date range bar ──────────────────── */}
      <div className="staff-filter-bar">
        <div className="staff-filter-left">
          <span className="staff-filter-label">Filter by:</span>
          <Dropdown
            menu={{ items: positionItems }}
            placement="bottomLeft"
            arrow={false}
            open={filterOpen}
            onOpenChange={isOpen => setFilterOpen(isOpen)}
            overlayClassName="custom-dropdown-overlay"
            overlayStyle={{ width: '220px' }}
          >
            <button
              className={`${filterOpen || positionFilter !== 'all' ? 'filter-btn-click' : 'filter-btn'} w-500`}
              onClick={() => setFilterOpen(v => !v)}
            >
              {positionLabels[positionFilter]} <Down />
            </button>
          </Dropdown>
        </div>

        <div className="staff-date-range-pill">
          <span className="staff-date-label">{formatWeekRange(weekStart)}</span>
          <button className="staff-date-nav" onClick={() => goWeek(-1)}>
            <CaretLeft size={16} color="#232626" />
          </button>
          <button className="staff-date-nav" onClick={() => goWeek(1)}>
            <CaretRight size={16} color="#232626" />
          </button>
        </div>
      </div>

      {/* ── Schedule panel ───────────────────────────── */}
      {isLoading ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : (
        <div className="staff-schedule-panel-wrap">
          <div className="staff-schedule-panel">
            {/* Left: Staff list */}
            <div className="staff-mentors-col">
              <div className="staff-mentors-header">Mentors</div>
              {filteredStaff.map(member => (
                <div key={member.key} className="staff-mentor-row">
                  <DotsSix
                    size={16}
                    weight="bold"
                    className="staff-drag-handle"
                  />
                  <Avatar size={32} className="staff-mentor-avatar">
                    {getNameSignature(member.name)}
                  </Avatar>
                  <div className="staff-mentor-info">
                    <span className="staff-mentor-name">{member.name}</span>
                    <span className="staff-mentor-sessions">
                      {member.role} &middot;{' '}
                      {member.sessions.length} session
                      {member.sessions.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              ))}

              {filteredStaff.length === 0 && (
                <p style={{ padding: '16px', color: '#939598', fontSize: 13 }}>
                  No staff found
                </p>
              )}
            </div>

            {/* Right: Schedule grid */}
            <div className="staff-schedule-col-wrap">
              <div className="staff-schedule-inner">
                {/* Time header */}
                <div className="staff-time-header">
                  {TIME_SLOTS.map(slot => (
                    <div key={slot} className="staff-time-cell">
                      {slot}
                    </div>
                  ))}
                </div>

                {/* Grid rows */}
                {filteredStaff.map(member => (
                  <div key={member.key} className="staff-grid-row">
                    {Array.from({ length: TOTAL_CELLS }, (_, i) => (
                      <div key={i} className="staff-grid-cell" />
                    ))}
                    {member.sessions.map(session => (
                      <div
                        key={session.id}
                        className={`staff-session-block staff-session-${session.color}`}
                        style={{
                          left: sessionLeft(session.startHour),
                          width: sessionWidth(session.startHour, session.endHour),
                        }}
                      >
                        <span className="staff-session-title">
                          {session.title}
                        </span>
                        <span className="staff-session-time">
                          {fmtHour(session.startHour)} -{' '}
                          {fmtHour(session.endHour)}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <RequestMentorModal />
    </div>
  )
}

export default StaffTab
